import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { Badge } from "@/components/ui/v2/badge";
import {
  Card,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/v2/card";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/v2/tooltip";
import { getCampaignStatus } from "@/features/campaigns/utils/getCampaignStatus";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { cn } from "@/lib/utils/cn";
import { CampaignJobBoardType } from "@/types/response/CampaignJobBoardType";
import { CampaignTypeDTO } from "@/types/response/CampaignType";

interface CampaignCardProps {
  campaign: CampaignTypeDTO;
  clientId?: string;
  queryString: string;
}

function CampaignCard({ campaign, queryString }: CampaignCardProps) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { canView } = usePermissionMatrix();
  const viewUrl = `./${campaign.id}/${queryString ? `?${queryString}` : ""}`;

  const integrations = campaign.integrations || [];
  const statusInfo = getCampaignStatus(integrations);

  // Get integration display names (integrationDisplayName or legacy jobBoardName), translated
  const jobBoardNames = integrations
    .filter(
      (jb: CampaignJobBoardType) =>
        jb.integrationDisplayName ?? jb.jobBoardName
    )
    .map((jb: CampaignJobBoardType) =>
      translateIntegrationDisplayName(
        jb.integrationDisplayName ?? jb.jobBoardName,
        t,
        i18n
      )
    );

  // Show first 2 jobboards, then a "+X" badge if there are more
  const displayIntegrations = jobBoardNames.slice(0, 2);
  const remainingCount = jobBoardNames.length - displayIntegrations.length;

  return (
    <Card
      hover={canView("campaigns")}
      className={cn(canView("campaigns") ? "cursor-pointer" : "cursor-default")}
      onClick={() => {
        if (canView("campaigns")) {
          navigate(viewUrl);
        }
      }}
    >
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="flex-1">{campaign.name}</CardTitle>
          <Badge variant={statusInfo.variant} size="medium">
            {t(statusInfo.labelKey)}
          </Badge>
        </div>
        {campaign.segmentName && (
          <p className="text-sm text-muted-foreground">
            {campaign.segmentName}
          </p>
        )}
      </CardHeader>
      {jobBoardNames.length > 0 && (
        <CardFooter className="flex-row">
          <div className="flex flex-wrap gap-2">
            {displayIntegrations.map((name: string) => (
              <Badge key={name} variant="outline" size="medium">
                {name}
              </Badge>
            ))}
            {remainingCount > 0 && (
              <Tooltip>
                <TooltipTrigger>
                  <Badge variant="outline" size="medium">
                    +{remainingCount}
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <div className="flex flex-col gap-1">
                    {jobBoardNames.slice(2, 7).map((name: string) => (
                      <Badge variant="outline" size="medium" key={name}>
                        {name}
                      </Badge>
                    ))}
                    {jobBoardNames.length > 7 && (
                      <div className="text-xs text-muted-foreground">
                        +{jobBoardNames.length - 7} more
                      </div>
                    )}
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
}

export default CampaignCard;
